// frontend-react/src/pages/UserManagementPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  message,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Popconfirm,
  Typography,
  Select,
  Row,
  Col,
  Switch,
  Tooltip,
  Spin,
  Upload,
  Progress,
  Alert,
  Divider,
  Steps,
  Statistic,
} from 'antd';
import {
  UserAddOutlined,
  UserOutlined,
  SettingOutlined,
  KeyOutlined,
  DeleteOutlined,
  EditOutlined,
  FileProtectOutlined,
  InboxOutlined,
  UploadOutlined,
  DownloadOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
  SortAscendingOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { getApiBaseUrl } from '../config/api';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

const { Title, Text } = Typography;

// ✅ 統一解析後端錯誤格式（支援：{ error: { code, message } } / { detail }）
async function parseApiError(response: Response) {
  let data: any = null;

  try {
    data = await response.json();
  } catch {
    // 有些錯誤回應可能沒有 body
  }

  const code = data?.error?.code;
  const message = data?.error?.message || data?.detail || data?.message || '請求失敗';

  return { status: response.status, code, message, raw: data };
}

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  position?: string;
  department_id?: number | null;
  department_name?: string;
  tags?: string[];
  notes?: string;
  is_active: boolean;
  created_at: string | null;
}

interface Department {
  id: number;
  name: string;
  manager_id?: number | null;
  manager_name?: string | null;
}

interface UserRulePermission {
  rule_id: number;
  rule_name: string;
  description: string;
  sources: string[];
  is_excluded: boolean;
  is_enabled: boolean;
}

interface UserRulePermissionsResponse {
  user_id: number;
  user_name: string;
  user_email: string;
  department_name: string | null;
  roles: string[];
  rules: UserRulePermission[];
}

// 批次匯入相關 Interface
interface BatchPreviewItem {
  row: number;
  name: string;
  email: string;
  phone?: string;
  position?: string;
  department_name?: string;
  tags?: string[];
  notes?: string;
  errors: string[];
  warnings: string[];
}

interface BatchPreviewResponse {
  total: number;
  valid_count: number;
  invalid_count: number;
  existing_departments: string[];
  preview: BatchPreviewItem[];
  has_more: boolean;
}

interface BatchImportResultItem {
  row: number;
  email: string;
  name: string;
  success: boolean;
  message: string;
  user_id?: number;
}

interface BatchImportResponse {
  total: number;
  success_count: number;
  failed_count: number;
  results: BatchImportResultItem[];
}

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditUserModalVisible, setIsEditUserModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedDeptOption, setSelectedDeptOption] = useState<string | number | null>(null);
  const [customDeptName, setCustomDeptName] = useState('');
  const [selectedPositionOption, setSelectedPositionOption] = useState<string | null>(null);
  const [customPositionName, setCustomPositionName] = useState('');
  const [editPositionOption, setEditPositionOption] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [editUserForm] = Form.useForm();
  const navigate = useNavigate();

  // 規則權限管理相關 state
  const [isRulePermissionModalVisible, setIsRulePermissionModalVisible] = useState(false);
  const [rulePermissionsLoading, setRulePermissionsLoading] = useState(false);
  const [userRulePermissions, setUserRulePermissions] =
    useState<UserRulePermissionsResponse | null>(null);
  const [togglingRuleId, setTogglingRuleId] = useState<number | null>(null);

  // 排序相關 state
  const [sortBy, setSortBy] = useState<string>('created_desc'); // 預設：建立時間（最新）

  // 批次匯入相關 state
  const [isBatchImportModalVisible, setIsBatchImportModalVisible] = useState(false);
  const [batchImportStep, setBatchImportStep] = useState(0); // 0: 上傳, 1: 預覽, 2: 結果
  const [batchFileList, setBatchFileList] = useState<UploadFile[]>([]);
  const [batchPreviewData, setBatchPreviewData] = useState<BatchPreviewResponse | null>(null);
  const [batchImportLoading, setBatchImportLoading] = useState(false);
  const [batchImportResult, setBatchImportResult] = useState<BatchImportResponse | null>(null);

  // const currentUser = authService.getUser(); // 未來可能需要用於權限檢查
  const token = authService.getToken();
  const { user: authUser, refreshUser } = useAuth();
  const { faqEnabled, riskLevelEnabled, imageDescriptionEnabled } = useFeatureFlags();
  const isAdmin =
    authUser?.is_superadmin ||
    authUser?.roles?.includes('ADMIN') ||
    authUser?.roles?.includes('SUPER_ADMIN');

  // 公司功能開關
  const [companyFeaturesSaving, setCompanyFeaturesSaving] = useState(false);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);

  const handleCompanyFeatureToggle = async (
    key: 'faq_enabled' | 'risk_level_enabled' | 'image_description_enabled',
    value: boolean
  ) => {
    setCompanyFeaturesSaving(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/v1/company/features`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: value }),
      });
      if (!res.ok) throw new Error('更新失敗');
      await refreshUser();
      message.success('功能設定已更新');
    } catch {
      message.error('更新功能設定失敗');
    } finally {
      setCompanyFeaturesSaving(false);
    }
  };

  // quota
  const [quotaUsed, setQuotaUsed] = useState<number | null>(null);
  const [quotaMax, setQuotaMax] = useState<number | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    fetchQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/company-users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('無法獲取使用者列表');
      }

      const data = await response.json();
      setUsers(data.users);
    } catch (error: any) {
      message.error(error.message || '載入使用者列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/departments/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('無法獲取部門列表');
      }

      const data = await response.json();
      setDepartments(data);
    } catch (error: any) {
      console.error('載入部門列表失敗:', error);
      // 不顯示錯誤訊息，因為部門列表是選填功能
    }
  };

  const fetchQuota = async () => {
    setQuotaLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/quota`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('無法獲取使用者名額');
      }

      const data = await response.json(); // { used: number, max: number | null }
      setQuotaUsed(typeof data.used === 'number' ? data.used : null);
      setQuotaMax(typeof data.max === 'number' ? data.max : null); // max 可能是 null
    } catch (error: any) {
      // quota 失敗不致命：不擋整頁
      console.error('載入使用者名額失敗:', error);
      setQuotaUsed(null);
      setQuotaMax(null);
    } finally {
      setQuotaLoading(false);
    }
  };

  // 預設職位選項
  const positionOptions = [
    { value: 'director', label: '總監' },
    { value: 'manager', label: '經理' },
    { value: 'legal', label: '法務' },
    { value: 'supervisor', label: '主管' },
    { value: 'senior_specialist', label: '資深專員' },
    { value: 'specialist', label: '專員' },
    { value: 'assistant', label: '助理' },
  ];

  // 批次匯入用：CSV 中文職稱 → 內部英文代碼
  const positionLabelToValue: Record<string, string> = {
    總監: 'director',
    經理: 'manager',
    法務: 'legal',
    主管: 'supervisor',
    資深專員: 'senior_specialist',
    專員: 'specialist',
    助理: 'assistant',
  };

  const openEditUserModal = (user: User) => {
    setSelectedUser(user);
    // 判斷職位是否為預設選項
    const isPresetPosition = positionOptions.some(opt => opt.value === user.position);
    setEditPositionOption(
      isPresetPosition ? user.position || null : user.position ? 'other' : null
    );

    editUserForm.setFieldsValue({
      name: user.name,
      phone: user.phone || '',
      position: isPresetPosition ? user.position : user.position ? 'other' : undefined,
      custom_position: isPresetPosition ? '' : user.position,
      department_id: user.department_id,
      tags: user.tags || [],
      notes: user.notes || '',
    });
    setIsEditUserModalVisible(true);
  };

  const handleUpdateUser = async (values: {
    name: string;
    phone?: string;
    position?: string;
    custom_position?: string;
    department_id?: number | null;
    tags?: string[];
    notes?: string;
  }) => {
    if (!selectedUser) return;

    try {
      // 處理職位邏輯
      let finalPosition = values.position;
      if (values.position === 'other' && values.custom_position) {
        finalPosition = values.custom_position;
      }

      const payload: Record<string, unknown> = {
        name: values.name,
        phone: values.phone || '',
        position: finalPosition || '',
        department_id: values.department_id === null ? 0 : values.department_id,
        tags: values.tags || [],
        notes: values.notes || '',
      };

      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/${selectedUser.id}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '更新使用者失敗');
      }

      const data = await response.json();
      message.success(data.message || '使用者已更新');
      setIsEditUserModalVisible(false);
      editUserForm.resetFields();
      setEditPositionOption(null);
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || '更新使用者失敗');
    }
  };

  const handleCreateUser = async (values: {
    name: string;
    email: string;
    phone?: string;
    position?: string;
    custom_position_name?: string;
    department_id?: number | string;
    custom_department_name?: string;
    tags?: string[];
    notes?: string;
  }) => {
    try {
      // 處理職位邏輯
      let finalPosition = values.position;
      if (values.position === 'other' && values.custom_position_name) {
        finalPosition = values.custom_position_name;
      }

      // 處理部門邏輯
      const payload: Record<string, unknown> = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        position: finalPosition,
        tags: values.tags,
        notes: values.notes,
      };

      if (values.department_id === 'other' && values.custom_department_name) {
        // 使用自訂部門名稱
        payload.department_name = values.custom_department_name;
      } else if (values.department_id && values.department_id !== 'other') {
        // 使用現有部門 ID
        payload.department_id = values.department_id;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/create-sub-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await parseApiError(response);

        // ✅ 名額超限：顯示清楚原因
        if (err.status === 409 && err.code === 'USER_QUOTA_EXCEEDED') {
          message.error(`建立失敗：${err.message}`);
          fetchQuota(); // 可選：刷新名額顯示
          return;
        }

        throw new Error(err.message || '新增使用者失敗');
      }

      message.success('使用者已新增，設定密碼的 Email 已發送');
      setIsCreateModalVisible(false);
      form.resetFields();
      setSelectedDeptOption(null);
      setCustomDeptName('');
      setSelectedPositionOption(null);
      setCustomPositionName('');
      fetchUsers();
      fetchQuota();
      fetchDepartments(); // 重新載入部門列表（如果新增了自訂部門）
    } catch (error: any) {
      message.error(error.message || '新增使用者失敗');
    }
  };

  const handleToggleStatus = async (userId: number) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/${userId}/toggle-status`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('無法更新使用者狀態');
      }

      const data = await response.json();
      message.success(data.message);
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || '更新狀態失敗');
    }
  };

  const handleManagePermissions = (userId: number) => {
    navigate(`/user-permission/${userId}`);
  };

  const handleResetPassword = async (userId: number, userEmail: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '重設密碼失敗');
      }

      await response.json(); // 消耗 response body
      message.success(`重設密碼的 Email 已發送至 ${userEmail}`);
    } catch (error: any) {
      message.error(error.message || '重設密碼失敗');
    }
  };

  const handleDeleteUser = async (userId: number, userName: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '刪除使用者失敗');
      }

      const data = await response.json();
      message.success(data.message || `使用者 ${userName} 已成功刪除`);
      fetchUsers(); // 重新載入使用者列表
      fetchQuota();
    } catch (error: any) {
      message.error(error.message || '刪除使用者失敗');
    }
  };

  // ========== 規則權限管理功能 ==========
  const fetchUserRulePermissions = async (userId: number) => {
    setRulePermissionsLoading(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/custom-rules/user/${userId}/rule-permissions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '獲取規則權限失敗');
      }

      const data = await response.json();
      setUserRulePermissions(data);
    } catch (error: any) {
      message.error(error.message || '載入規則權限失敗');
    } finally {
      setRulePermissionsLoading(false);
    }
  };

  const openRulePermissionModal = (user: User) => {
    setSelectedUser(user);
    setIsRulePermissionModalVisible(true);
    fetchUserRulePermissions(user.id);
  };

  const handleToggleRulePermission = async (ruleId: number, currentEnabled: boolean) => {
    if (!selectedUser) return;

    setTogglingRuleId(ruleId);
    try {
      let response;

      if (currentEnabled) {
        // 目前啟用 -> 要排除
        response = await fetch(
          `${getApiBaseUrl()}/api/custom-rules/user/${selectedUser.id}/rule-permissions/exclude?rule_id=${ruleId}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        // 目前停用 -> 要啟用（移除排除或直接授予）
        // 先嘗試移除排除
        response = await fetch(
          `${getApiBaseUrl()}/api/custom-rules/user/${selectedUser.id}/rule-permissions/exclude?rule_id=${ruleId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // 如果沒有排除記錄，則直接授予權限
        if (response.ok) {
          const result = await response.json();
          if (result.message.includes('並未被排除')) {
            // 直接授予權限
            response = await fetch(
              `${getApiBaseUrl()}/api/custom-rules/user/${selectedUser.id}/rule-permissions/grant?rule_id=${ruleId}`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          }
        }
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '更新權限失敗');
      }

      const data = await response.json();
      message.success(data.message);

      // 重新載入權限列表
      fetchUserRulePermissions(selectedUser.id);
    } catch (error: any) {
      message.error(error.message || '更新權限失敗');
    } finally {
      setTogglingRuleId(null);
    }
  };

  const getSourceLabel = (source: string): string => {
    const labels: Record<string, string> = {
      personal: '個人授權',
      owner: '自己上傳',
      public: '公開規則',
      department: '部門分享',
      role: '角色授權',
    };
    return labels[source] || source;
  };

  const getSourceColor = (source: string): string => {
    const colors: Record<string, string> = {
      personal: 'purple',
      owner: 'gold',
      public: 'green',
      department: 'blue',
      role: 'cyan',
    };
    return colors[source] || 'default';
  };

  // ========== 排序功能 ==========

  // 排序選項
  const sortOptions = [
    { value: 'created_desc', label: '建立時間（最新）' },
    { value: 'created_asc', label: '建立時間（最舊）' },
    { value: 'name_asc', label: '姓名（A → Z）' },
    { value: 'name_desc', label: '姓名（Z → A）' },
    { value: 'email_asc', label: 'Email（A → Z）' },
    { value: 'email_desc', label: 'Email（Z → A）' },
    { value: 'department_asc', label: '部門（A → Z）' },
    { value: 'status_active', label: '狀態（啟用優先）' },
    { value: 'status_inactive', label: '狀態（停用優先）' },
  ];

  // 排序後的使用者列表
  const sortedUsers = React.useMemo(() => {
    const sorted = [...users];

    switch (sortBy) {
      case 'created_desc':
        return sorted.sort((a, b) => {
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
      case 'created_asc':
        return sorted.sort((a, b) => {
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });
      case 'name_asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name, 'zh-TW'));
      case 'name_desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name, 'zh-TW'));
      case 'email_asc':
        return sorted.sort((a, b) => a.email.localeCompare(b.email));
      case 'email_desc':
        return sorted.sort((a, b) => b.email.localeCompare(a.email));
      case 'department_asc':
        return sorted.sort((a, b) => {
          const deptA = a.department_name || '';
          const deptB = b.department_name || '';
          return deptA.localeCompare(deptB, 'zh-TW');
        });
      case 'status_active':
        return sorted.sort((a, b) => {
          if (a.is_active === b.is_active) return 0;
          return a.is_active ? -1 : 1;
        });
      case 'status_inactive':
        return sorted.sort((a, b) => {
          if (a.is_active === b.is_active) return 0;
          return a.is_active ? 1 : -1;
        });
      default:
        return sorted;
    }
  }, [users, sortBy]);

  // ========== 批次匯入功能 ==========

  // 下載 CSV 範本
  const handleDownloadTemplate = () => {
    window.open(`${getApiBaseUrl()}/api/v1/user/batch-import/template`, '_blank');
  };

  // 預覽 CSV 檔案
  const handleBatchPreview = async (file: File) => {
    setBatchImportLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/batch-import/preview`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const err = await parseApiError(response);
        throw new Error(err.message || '預覽失敗');
      }

      const data: BatchPreviewResponse = await response.json();
      setBatchPreviewData(data);
      setBatchImportStep(1); // 進入預覽步驟
    } catch (error: any) {
      message.error(error.message || '預覽 CSV 檔案失敗');
    } finally {
      setBatchImportLoading(false);
    }
  };

  // 執行批次匯入
  const handleBatchImport = async () => {
    if (!batchPreviewData) return;

    // 過濾出沒有錯誤的資料，並將職位中文名稱轉成內部代碼
    const validUsers = batchPreviewData.preview
      .filter(item => item.errors.length === 0)
      .map(item => {
        // 原始職位字串（可能是「經理」、「助理」，也可能本來就是英文代碼或空值）
        const rawPosition = item.position?.trim();

        // 若是已知的「中文職稱」→ 轉成對應英文代碼
        // 若不是 mapping 內的值 → 保留原字串（當成自訂職位）
        const finalPosition =
          rawPosition && positionLabelToValue[rawPosition]
            ? positionLabelToValue[rawPosition]
            : rawPosition || undefined;

        return {
          name: item.name,
          email: item.email,
          phone: item.phone || undefined,
          position: finalPosition,
          department_name: item.department_name || undefined,
          tags: item.tags || undefined,
          notes: item.notes || undefined,
        };
      });

    if (validUsers.length === 0) {
      message.error('沒有可匯入的有效資料');
      return;
    }

    setBatchImportLoading(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/user/batch-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ users: validUsers }),
      });

      if (!response.ok) {
        const err = await parseApiError(response);

        if (err.status === 409 && err.code === 'USER_QUOTA_EXCEEDED') {
          message.error(`匯入失敗：${err.message}`);
          fetchQuota();
          return;
        }

        throw new Error(err.message || '批次匯入失敗');
      }

      const data: BatchImportResponse = await response.json();
      setBatchImportResult(data);
      setBatchImportStep(2); // 進入結果步驟

      // 重新載入使用者列表
      fetchUsers();
      fetchDepartments();
      fetchQuota();
    } catch (error: any) {
      message.error(error.message || '批次匯入失敗');
    } finally {
      setBatchImportLoading(false);
    }
  };

  // 重設批次匯入狀態
  const resetBatchImport = () => {
    setBatchImportStep(0);
    setBatchFileList([]);
    setBatchPreviewData(null);
    setBatchImportResult(null);
    setBatchImportLoading(false);
  };

  // 關閉批次匯入 Modal
  const closeBatchImportModal = () => {
    setIsBatchImportModalVisible(false);
    resetBatchImport();
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      render: (name: string) => (
        <Space>
          <UserOutlined />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '職位',
      dataIndex: 'position',
      key: 'position',
      width: 100,
      render: (position: string) => {
        const positionMap: Record<string, string> = {
          director: '總監',
          manager: '經理',
          legal: '法務',
          supervisor: '主管',
          senior_specialist: '資深專員',
          specialist: '專員',
          assistant: '助理',
        };
        return position ? positionMap[position] || position : '-';
      },
    },
    {
      title: '部門',
      dataIndex: 'department_name',
      key: 'department_name',
      width: 150,
      render: (dept: string) => <Text>{dept || '-'}</Text>,
    },

    {
      title: '標籤',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags: string[]) => (
        <>
          {tags && tags.length > 0 ? (
            tags.map((tag, index) => (
              <Tag key={index} color='blue' style={{ marginBottom: 4 }}>
                {tag}
              </Tag>
            ))
          ) : (
            <Text type='secondary'>-</Text>
          )}
        </>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive: boolean, record: User) => (
        <Popconfirm
          title={isActive ? '停用帳號' : '啟用帳號'}
          description={`確定要${isActive ? '停用' : '啟用'}「${record.name}」嗎？`}
          onConfirm={() => handleToggleStatus(record.id)}
          okText='確定'
          cancelText='取消'
          okButtonProps={isActive ? { danger: true } : {}}
        >
          <Tag
            color={isActive ? '#0A4A5E' : '#00000040'}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {isActive ? '啟用' : '停用'}
          </Tag>
        </Popconfirm>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Button type='link' icon={<EditOutlined />} onClick={() => openEditUserModal(record)}>
            編輯
          </Button>
          <Button
            type='link'
            icon={<FileProtectOutlined />}
            onClick={() => openRulePermissionModal(record)}
          >
            規則權限
          </Button>
          <Button
            type='link'
            icon={<SettingOutlined />}
            onClick={() => handleManagePermissions(record.id)}
          >
            權限設定
          </Button>
          <Popconfirm
            title={`確定要發送重設密碼的 Email 給 ${record.email} 嗎？`}
            description='使用者將收到一封包含重設密碼連結的 Email（有效期限 24 小時）'
            onConfirm={() => handleResetPassword(record.id, record.email)}
            okText='確定發送'
            cancelText='取消'
          >
            <Button type='link' icon={<KeyOutlined />}>
              重設密碼
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`⚠️ 確定要刪除使用者「${record.name}」嗎？`}
            description={
              <div>
                <p style={{ marginBottom: 8, color: '#ff4d4f', fontWeight: 'bold' }}>
                  ⚠️ 此操作無法復原！
                </p>
                <p style={{ marginBottom: 4 }}>將會刪除：</p>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  <li>使用者帳號和所有個人資料</li>
                  <li>登入會話記錄</li>
                  <li>權限設定</li>
                  <li>所有相關的審閱和比對記錄</li>
                </ul>
                <p style={{ marginTop: 8, color: '#666' }}>Email: {record.email}</p>
              </div>
            }
            onConfirm={() => handleDeleteUser(record.id, record.name)}
            okText='確定刪除'
            cancelText='取消'
            okButtonProps={{ danger: true }}
          >
            <Button type='link' danger icon={<DeleteOutlined />}>
              刪除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout>
      <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
        <Card>
          <div
            style={{
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <Title level={2} style={{ margin: 0 }}>
                帳號與權限管理
              </Title>

              <Space direction='vertical' size={2}>
                <Text type='secondary'>
                  已註冊帳號：
                  {quotaLoading ? (
                    <Spin size='small' style={{ marginLeft: 8 }} />
                  ) : quotaUsed === null ? (
                    <span style={{ marginLeft: 8 }}>-</span>
                  ) : quotaMax === null ? (
                    <span style={{ marginLeft: 8 }}>{quotaUsed} / 不限</span>
                  ) : (
                    <span style={{ marginLeft: 8 }}>
                      {quotaUsed} / {quotaMax}
                    </span>
                  )}
                </Text>
              </Space>
            </div>

            <Space size='middle'>
              <Space.Compact>
                <Button icon={<SortAscendingOutlined />} style={{ pointerEvents: 'none' }}>
                  排序
                </Button>
                <Select
                  value={sortBy}
                  onChange={setSortBy}
                  style={{ width: 160 }}
                  options={sortOptions}
                />
              </Space.Compact>
              <Button
                type='primary'
                icon={<UserAddOutlined />}
                onClick={() => setIsCreateModalVisible(true)}
              >
                新增使用者
              </Button>
              <Button icon={<UploadOutlined />} onClick={() => setIsBatchImportModalVisible(true)}>
                批次匯入
              </Button>
              {isAdmin && (
                <Button
                  type='primary'
                  icon={<SettingOutlined />}
                  onClick={() => setFeatureModalOpen(true)}
                >
                  網頁功能
                </Button>
              )}
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={sortedUsers}
            rowKey='id'
            loading={loading}
            scroll={{ x: 1400 }}
            pagination={{
              pageSize: 10,
              showTotal: total => `共 ${total} 個使用者`,
            }}
          />
        </Card>
      </div>

      <Modal
        title='新增使用者'
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
          setSelectedDeptOption(null);
          setCustomDeptName('');
          setSelectedPositionOption(null);
          setCustomPositionName('');
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout='vertical' onFinish={handleCreateUser}>
          {/* 必填欄位 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label='姓名'
                name='name'
                rules={[{ required: true, message: '請輸入姓名' }]}
              >
                <Input placeholder='請輸入使用者姓名' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='Email'
                name='email'
                rules={[
                  { required: true, message: '請輸入 Email' },
                  { type: 'email', message: '請輸入有效的 Email' },
                ]}
              >
                <Input placeholder='user@example.com' />
              </Form.Item>
            </Col>
          </Row>

          {/* 選填欄位 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label='手機號碼' name='phone' tooltip='選填，方便聯繫使用者'>
                <Input placeholder='0912-345-678' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='職位' name='position' tooltip='選填，例如：經理、專員、主管、法務'>
                <Select
                  placeholder='請選擇職位'
                  allowClear
                  onChange={value => {
                    setSelectedPositionOption(value);
                    if (value !== 'other') {
                      setCustomPositionName('');
                    }
                  }}
                >
                  <Select.Option value='director'>總監</Select.Option>
                  <Select.Option value='manager'>經理</Select.Option>
                  <Select.Option value='legal'>法務</Select.Option>
                  <Select.Option value='supervisor'>主管</Select.Option>
                  <Select.Option value='senior_specialist'>資深專員</Select.Option>
                  <Select.Option value='specialist'>專員</Select.Option>
                  <Select.Option value='assistant'>助理</Select.Option>
                  <Select.Option value='other'>其他（自訂職位）</Select.Option>
                </Select>
              </Form.Item>
              {selectedPositionOption === 'other' && (
                <Form.Item
                  label='自訂職位名稱'
                  name='custom_position_name'
                  rules={[{ required: true, message: '請輸入職位名稱' }]}
                >
                  <Input
                    placeholder='請輸入職位名稱'
                    value={customPositionName}
                    onChange={e => setCustomPositionName(e.target.value)}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label='部門'
                name='department_id'
                rules={[{ required: true, message: '請選擇部門' }]}
                tooltip='必填，使用者必須歸屬於一個部門'
              >
                <Select
                  placeholder='請選擇部門'
                  onChange={value => {
                    setSelectedDeptOption(value);
                    if (value !== 'other') {
                      setCustomDeptName('');
                    }
                  }}
                >
                  {departments.map(dept => (
                    <Select.Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Select.Option>
                  ))}
                  <Select.Option value='other'>其他（自訂部門名稱）</Select.Option>
                </Select>
              </Form.Item>
              {selectedDeptOption === 'other' && (
                <Form.Item
                  label='自訂部門名稱'
                  name='custom_department_name'
                  rules={[{ required: true, message: '請輸入部門名稱' }]}
                >
                  <Input
                    placeholder='請輸入新部門名稱'
                    value={customDeptName}
                    onChange={e => setCustomDeptName(e.target.value)}
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={12}>
              <Form.Item label='標籤' name='tags' tooltip='選填，可多選，方便分類管理'>
                <Select mode='tags' placeholder='請選擇或輸入標籤' allowClear>
                  <Select.Option value='VIP'>VIP</Select.Option>
                  <Select.Option value='測試帳號'>測試帳號</Select.Option>
                  <Select.Option value='臨時帳號'>臨時帳號</Select.Option>
                  <Select.Option value='外部顧問'>外部顧問</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label='備註' name='notes' tooltip='選填，可記錄其他相關資訊'>
            <Input.TextArea
              placeholder='請輸入備註資訊（例如：負責項目、特殊權限需求等）'
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setIsCreateModalVisible(false);
                  form.resetFields();
                  setSelectedDeptOption(null);
                  setCustomDeptName('');
                  setSelectedPositionOption(null);
                  setCustomPositionName('');
                }}
              >
                取消
              </Button>
              <Button type='primary' htmlType='submit'>
                新增
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <div
          style={{ marginTop: '16px', padding: '12px', background: '#f0f2f5', borderRadius: '4px' }}
        >
          <Text type='secondary' style={{ fontSize: '12px' }}>
            💡 新增後，系統會自動發送設定密碼的 Email 給該使用者。使用者會自動加入所選部門。
          </Text>
        </div>
      </Modal>

      {/* 編輯使用者 Modal */}
      <Modal
        title={`編輯使用者 - ${selectedUser?.name}`}
        open={isEditUserModalVisible}
        onCancel={() => {
          setIsEditUserModalVisible(false);
          editUserForm.resetFields();
          setEditPositionOption(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={editUserForm} layout='vertical' onFinish={handleUpdateUser}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label='姓名'
                name='name'
                rules={[{ required: true, message: '請輸入姓名' }]}
              >
                <Input placeholder='請輸入使用者姓名' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='Email'>
                <Input value={selectedUser?.email} disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label='手機號碼' name='phone'>
                <Input placeholder='0912-345-678' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='職位' name='position'>
                <Select
                  placeholder='請選擇職位'
                  allowClear
                  onChange={value => {
                    setEditPositionOption(value);
                  }}
                >
                  {positionOptions.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                  <Select.Option value='other'>其他（自訂職位）</Select.Option>
                </Select>
              </Form.Item>
              {editPositionOption === 'other' && (
                <Form.Item
                  label='自訂職位名稱'
                  name='custom_position'
                  rules={[{ required: true, message: '請輸入職位名稱' }]}
                >
                  <Input placeholder='請輸入職位名稱' />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label='部門' name='department_id'>
                <Select placeholder='請選擇部門' allowClear showSearch optionFilterProp='children'>
                  <Select.Option value={null}>（不屬於任何部門）</Select.Option>
                  {departments.map(dept => (
                    <Select.Option key={dept.id} value={dept.id}>
                      {dept.name}
                      {dept.manager_name && ` - 主管：${dept.manager_name}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='標籤' name='tags'>
                <Select mode='tags' placeholder='請選擇或輸入標籤' allowClear>
                  <Select.Option value='VIP'>VIP</Select.Option>
                  <Select.Option value='測試帳號'>測試帳號</Select.Option>
                  <Select.Option value='臨時帳號'>臨時帳號</Select.Option>
                  <Select.Option value='外部顧問'>外部顧問</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label='備註' name='notes'>
            <Input.TextArea placeholder='請輸入備註資訊' rows={3} maxLength={500} showCount />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setIsEditUserModalVisible(false);
                  editUserForm.resetFields();
                  setEditPositionOption(null);
                }}
              >
                取消
              </Button>
              <Button type='primary' htmlType='submit'>
                儲存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 規則權限管理 Modal */}
      <Modal
        title={
          <Space>
            <FileProtectOutlined />
            <span>規則權限管理 - {selectedUser?.name}</span>
          </Space>
        }
        open={isRulePermissionModalVisible}
        onCancel={() => {
          setIsRulePermissionModalVisible(false);
          setUserRulePermissions(null);
        }}
        footer={[
          <Button
            key='close'
            onClick={() => {
              setIsRulePermissionModalVisible(false);
              setUserRulePermissions(null);
            }}
          >
            關閉
          </Button>,
        ]}
        width={700}
      >
        {rulePermissionsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size='large' />
            <div style={{ marginTop: 16 }}>載入中...</div>
          </div>
        ) : userRulePermissions ? (
          <div>
            {/* 使用者資訊 */}
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 4,
              }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Text type='secondary'>Email：</Text>
                  <Text>{userRulePermissions.user_email}</Text>
                </Col>
                <Col span={8}>
                  <Text type='secondary'>部門：</Text>
                  <Text>{userRulePermissions.department_name || '無'}</Text>
                </Col>
                <Col span={8}>
                  <Text type='secondary'>角色：</Text>
                  <Text>{userRulePermissions.roles.join(', ') || '無'}</Text>
                </Col>
              </Row>
            </div>

            {/* 規則列表 */}
            {userRulePermissions.rules.length > 0 ? (
              <Table
                dataSource={userRulePermissions.rules}
                rowKey='rule_id'
                pagination={false}
                size='small'
                columns={[
                  {
                    title: '規則名稱',
                    dataIndex: 'rule_name',
                    key: 'rule_name',
                    width: 200,
                    render: (name: string, record: UserRulePermission) => (
                      <Tooltip title={record.description}>
                        <span>{name}</span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: '權限來源',
                    dataIndex: 'sources',
                    key: 'sources',
                    width: 250,
                    render: (sources: string[]) => (
                      <Space wrap size={[0, 4]}>
                        {sources.map(source => (
                          <Tag key={source} color={getSourceColor(source)}>
                            {getSourceLabel(source)}
                          </Tag>
                        ))}
                        {sources.length === 0 && <Text type='secondary'>無權限來源</Text>}
                      </Space>
                    ),
                  },
                  {
                    title: '狀態',
                    dataIndex: 'is_enabled',
                    key: 'is_enabled',
                    width: 100,
                    render: (isEnabled: boolean, record: UserRulePermission) => (
                      <Space>
                        <Switch
                          checked={isEnabled}
                          loading={togglingRuleId === record.rule_id}
                          onChange={() => handleToggleRulePermission(record.rule_id, isEnabled)}
                        />
                        <Text type={isEnabled ? 'success' : 'secondary'}>
                          {isEnabled ? '啟用' : '停用'}
                        </Text>
                      </Space>
                    ),
                  },
                ]}
              />
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: 40,
                  color: '#999',
                }}
              >
                此使用者目前沒有任何規則權限
              </div>
            )}

            {/* 說明 */}
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: '#e6f4f8',
                borderRadius: 4,
              }}
            >
              <Text type='secondary' style={{ fontSize: 12 }}>
                💡 說明：
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                  <li>
                    <Tag color='blue'>部門分享</Tag> 表示該規則已分享給使用者所屬部門
                  </li>
                  <li>
                    <Tag color='gold'>自己上傳</Tag> 表示使用者自己上傳的規則
                  </li>
                  <li>
                    <Tag color='purple'>個人授權</Tag> 表示 Admin 單獨授予此使用者的權限
                  </li>
                  <li>關閉開關會將該使用者從此規則「排除」，即使部門有權限也無法使用</li>
                </ul>
              </Text>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            無法載入規則權限資訊
          </div>
        )}
      </Modal>

      {/* 批次匯入 Modal */}
      <Modal
        title='批次匯入使用者'
        open={isBatchImportModalVisible}
        onCancel={closeBatchImportModal}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Steps
          current={batchImportStep}
          items={[{ title: '上傳檔案' }, { title: '預覽確認' }, { title: '匯入結果' }]}
          style={{ marginBottom: 24 }}
        />

        {/* Step 0: 上傳檔案 */}
        {batchImportStep === 0 && (
          <div>
            <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 13, lineHeight: '1.8' }}>
              <p style={{ margin: '0 0 4px 0' }}>
                請上傳 CSV 格式的檔案，必要欄位為 <Text code>姓名</Text> 和 <Text code>Email</Text>
                。
              </p>
              <p style={{ margin: '0 0 4px 0' }}>
                可選欄位：<Text code>手機</Text>、<Text code>職位</Text>、<Text code>部門</Text>、
                <Text code>標籤</Text>、<Text code>備註</Text>
              </p>
              <p style={{ margin: 0 }}>標籤欄位若有多個標籤，請用逗號分隔（例如：VIP,重要客戶）</p>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                下載 CSV 範本
              </Button>
            </div>

            <Upload.Dragger
              accept='.csv'
              fileList={batchFileList}
              beforeUpload={file => {
                setBatchFileList([file as unknown as UploadFile]);
                handleBatchPreview(file);
                return false; // 阻止自動上傳
              }}
              onRemove={() => {
                setBatchFileList([]);
                setBatchPreviewData(null);
              }}
              maxCount={1}
            >
              <p className='ant-upload-drag-icon'>
                <InboxOutlined style={{ fontSize: 48, color: 'var(--color-warning)' }} />
              </p>
              <p className='ant-upload-text'>點擊或拖曳 CSV 檔案到此區域</p>
              <p className='ant-upload-hint'>僅支援 .csv 格式，單次最多匯入 100 筆資料</p>
            </Upload.Dragger>

            {batchImportLoading && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Spin tip='正在解析檔案...' />
              </div>
            )}
          </div>
        )}

        {/* Step 1: 預覽確認 */}
        {batchImportStep === 1 && batchPreviewData && (
          <div>
            {/* 統計資訊 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Card size='small'>
                  <Statistic title='總筆數' value={batchPreviewData.total} />
                </Card>
              </Col>
              <Col span={8}>
                <Card size='small'>
                  <Statistic
                    title='可匯入'
                    value={batchPreviewData.valid_count}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<CheckCircleFilled />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size='small'>
                  <Statistic
                    title='錯誤'
                    value={batchPreviewData.invalid_count}
                    valueStyle={{
                      color: batchPreviewData.invalid_count > 0 ? '#ff4d4f' : undefined,
                    }}
                    prefix={batchPreviewData.invalid_count > 0 ? <CloseCircleFilled /> : undefined}
                  />
                </Card>
              </Col>
            </Row>

            {batchPreviewData.has_more && (
              <Alert
                message={`共 ${batchPreviewData.total} 筆資料，僅顯示前 50 筆預覽`}
                type='warning'
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* 預覽表格 */}
            <Table
              dataSource={batchPreviewData.preview}
              rowKey='row'
              size='small'
              scroll={{ x: 800, y: 400 }}
              pagination={false}
              columns={[
                {
                  title: '序號',
                  dataIndex: 'row',
                  width: 60,
                },
                {
                  title: '姓名',
                  dataIndex: 'name',
                  width: 100,
                },
                {
                  title: 'Email',
                  dataIndex: 'email',
                  width: 180,
                },
                {
                  title: '部門',
                  dataIndex: 'department_name',
                  width: 100,
                  render: (dept: string) => dept || '-',
                },
                {
                  title: '職位',
                  dataIndex: 'position',
                  width: 80,
                  render: (pos: string) => pos || '-',
                },
                {
                  title: '狀態',
                  key: 'status',
                  width: 200,
                  render: (_: any, record: BatchPreviewItem) => (
                    <div>
                      {record.errors.length > 0 && (
                        <div>
                          {record.errors.map((err, idx) => (
                            <Tag key={idx} color='red' style={{ marginBottom: 4 }}>
                              <CloseCircleFilled /> {err}
                            </Tag>
                          ))}
                        </div>
                      )}
                      {record.warnings.length > 0 && (
                        <div>
                          {record.warnings.map((warn, idx) => (
                            <Tag key={idx} color='orange' style={{ marginBottom: 4 }}>
                              <WarningFilled /> {warn}
                            </Tag>
                          ))}
                        </div>
                      )}
                      {record.errors.length === 0 && record.warnings.length === 0 && (
                        <Tag color='green'>
                          <CheckCircleFilled /> 可匯入
                        </Tag>
                      )}
                    </div>
                  ),
                },
              ]}
            />

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={() => {
                  setBatchImportStep(0);
                  setBatchPreviewData(null);
                  setBatchFileList([]);
                }}
              >
                重新上傳
              </Button>
              <Space>
                <Button onClick={closeBatchImportModal}>取消</Button>
                <Button
                  type='primary'
                  onClick={handleBatchImport}
                  loading={batchImportLoading}
                  disabled={batchPreviewData.valid_count === 0}
                >
                  確認匯入 ({batchPreviewData.valid_count} 筆)
                </Button>
              </Space>
            </div>
          </div>
        )}

        {/* Step 2: 匯入結果 */}
        {batchImportStep === 2 && batchImportResult && (
          <div>
            {/* 結果統計 */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Progress
                type='circle'
                percent={Math.round(
                  (batchImportResult.success_count / batchImportResult.total) * 100
                )}
                format={() => `${batchImportResult.success_count}/${batchImportResult.total}`}
                status={batchImportResult.failed_count > 0 ? 'exception' : 'success'}
              />
              <div style={{ marginTop: 16 }}>
                <Text strong style={{ fontSize: 16 }}>
                  成功匯入 {batchImportResult.success_count} 筆
                  {batchImportResult.failed_count > 0 && (
                    <span style={{ color: '#ff4d4f' }}>
                      ，失敗 {batchImportResult.failed_count} 筆
                    </span>
                  )}
                </Text>
              </div>
            </div>

            {/* 詳細結果 */}
            <Table
              dataSource={batchImportResult.results}
              rowKey='row'
              size='small'
              scroll={{ y: 300 }}
              pagination={false}
              columns={[
                {
                  title: '序號',
                  dataIndex: 'row',
                  width: 60,
                },
                {
                  title: '姓名',
                  dataIndex: 'name',
                  width: 100,
                },
                {
                  title: 'Email',
                  dataIndex: 'email',
                  width: 200,
                },
                {
                  title: '結果',
                  key: 'result',
                  render: (_: any, record: BatchImportResultItem) => (
                    <Space>
                      {record.success ? (
                        <Tag color='green' icon={<CheckCircleFilled />}>
                          成功
                        </Tag>
                      ) : (
                        <Tag color='red' icon={<CloseCircleFilled />}>
                          失敗
                        </Tag>
                      )}
                      {record.success ? (
                        <Text>{record.message}</Text>
                      ) : (
                        <Text type='danger'>{record.message}</Text>
                      )}
                    </Space>
                  ),
                },
              ]}
            />

            <Divider />

            <div style={{ textAlign: 'right' }}>
              <Button type='primary' onClick={closeBatchImportModal}>
                完成
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 網頁功能 Modal */}
      <Modal
        title='網頁功能'
        open={featureModalOpen}
        onCancel={() => setFeatureModalOpen(false)}
        footer={null}
        width={360}
      >
        <Space direction='vertical' size={20} style={{ width: '100%', padding: '8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>顯示常見問答</span>
            <Switch
              checked={faqEnabled}
              onChange={val => handleCompanyFeatureToggle('faq_enabled', val)}
              loading={companyFeaturesSaving}
              checkedChildren='開啟'
              unCheckedChildren='關閉'
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>顯示風險等級</span>
            <Switch
              checked={riskLevelEnabled}
              onChange={val => handleCompanyFeatureToggle('risk_level_enabled', val)}
              loading={companyFeaturesSaving}
              checkedChildren='開啟'
              unCheckedChildren='關閉'
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>顯示圖片轉文字</span>
            <Switch
              checked={imageDescriptionEnabled}
              onChange={val => handleCompanyFeatureToggle('image_description_enabled', val)}
              loading={companyFeaturesSaving}
              checkedChildren='開啟'
              unCheckedChildren='關閉'
            />
          </div>
        </Space>
      </Modal>
    </PageLayout>
  );
};

export default UserManagementPage;
